import React, { useState, useEffect, useCallback } from 'react';
import { Segment, Header, Statistic, Loader, Message, Grid, Dropdown, Button } from 'semantic-ui-react';
import axiosInstance from '../../lib/auth/axiosApi';

const MonarchMonthlyAverages = () => {
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState(12);

  const monthOptions = [
    { key: 3, text: '3 months', value: 3 },
    { key: 6, text: '6 months', value: 6 },
    { key: 12, text: '12 months', value: 12 },
    { key: 18, text: '18 months', value: 18 },
    { key: 24, text: '24 months', value: 24 },
    { key: 36, text: '36 months', value: 36 },
  ];

  const fetchMonthlyAverages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/monarch/monthly_averages/?months=${selectedMonths}`);
      setMonthlyData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch monthly averages');
    } finally {
      setLoading(false);
    }
  }, [selectedMonths]);

  useEffect(() => {
    fetchMonthlyAverages();
  }, [fetchMonthlyAverages]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleMonthsChange = (e, { value }) => {
    setSelectedMonths(value);
  };

  if (loading) {
    return (
      <Segment style={{ minHeight: '300px' }}>
        <Loader active>Loading monthly averages...</Loader>
      </Segment>
    );
  }

  if (error) {
    return (
      <Message negative>
        <Message.Header>Error</Message.Header>
        <p>{error}</p>
        <Button onClick={fetchMonthlyAverages} primary>
          Retry
        </Button>
      </Message>
    );
  }

  const netCashFlow = (monthlyData?.averageIncome || 0) - (monthlyData?.averageExpenses || 0);

  return (
    <div>
      <Segment>
        <Grid>
          <Grid.Column width={12}>
            <Header as="h3">Monthly Averages</Header>
          </Grid.Column>
          <Grid.Column width={4} textAlign="right">
            <Dropdown
              selection
              value={selectedMonths}
              options={monthOptions}
              onChange={handleMonthsChange}
              style={{ minWidth: '120px' }}
            />
          </Grid.Column>
        </Grid>
      </Segment>

      <Grid columns={3} stackable>
        <Grid.Column>
          <Segment textAlign="center" style={{ padding: '2rem' }}>
            <Statistic color="green" size="small">
              <Statistic.Value>
                {formatCurrency(monthlyData?.averageIncome || 0)}
              </Statistic.Value>
              <Statistic.Label style={{ marginTop: '0.5rem' }}>AVG MONTHLY INCOME</Statistic.Label>
            </Statistic>
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment textAlign="center" style={{ padding: '2rem' }}>
            <Statistic color="red" size="small">
              <Statistic.Value>
                {formatCurrency(monthlyData?.averageExpenses || 0)}
              </Statistic.Value>
              <Statistic.Label style={{ marginTop: '0.5rem' }}>AVG MONTHLY EXPENSES</Statistic.Label>
            </Statistic>
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment textAlign="center" style={{ padding: '2rem' }}>
            <Statistic color={netCashFlow >= 0 ? 'blue' : 'orange'} size="small">
              <Statistic.Value>
                {formatCurrency(netCashFlow)}
              </Statistic.Value>
              <Statistic.Label style={{ marginTop: '0.5rem' }}>NET CASH FLOW</Statistic.Label>
            </Statistic>
          </Segment>
        </Grid.Column>
      </Grid>

      <Segment>
        <Header as="h4">
          Analysis Summary ({monthlyData?.monthsAnalyzed || 0} months analyzed)
        </Header>
        <p>
          <strong>Time Period:</strong> Last {selectedMonths} months<br/>
          <strong>Data Points:</strong> {monthlyData?.monthsAnalyzed || 0} months with transaction data<br/>
          <strong>Cash Flow Status:</strong> {netCashFlow >= 0 ? 'Positive' : 'Negative'} monthly cash flow
        </p>
      </Segment>

      {monthlyData?.monthlyBreakdown && monthlyData.monthlyBreakdown.length > 0 && (
        <Segment>
          <Header as="h4">Monthly Breakdown</Header>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {monthlyData.monthlyBreakdown.map((month, index) => (
              <Grid key={index} columns={3} style={{ marginBottom: '8px', padding: '8px', backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' }}>
                <Grid.Column width={6}>
                  <strong>{month.month}</strong>
                </Grid.Column>
                <Grid.Column width={5} style={{ color: 'green' }}>
                  Income: {formatCurrency(month.income)}
                </Grid.Column>
                <Grid.Column width={5} style={{ color: 'red' }}>
                  Expenses: {formatCurrency(month.expenses)}
                </Grid.Column>
              </Grid>
            ))}
          </div>
        </Segment>
      )}
    </div>
  );
};

export default MonarchMonthlyAverages;